import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';

const formCss = ":host{position:relative;box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{display:flex;flex-direction:column;gap:1.5rem;font:var(--tt-font-standard)}";

const formControlAccessDefinition = {
  'tt-button': {
    serialize: async (el) => {
      if (el.name && !el.disabled) {
        return { [el.name]: el.value };
      }
    },
    appendFormData: async (data, el) => {
      if (el.name && !el.disabled) {
        data.append(el.name, el.value);
      }
    },
    click: (event, form) => {
      if (event.target.submit) {
        form.submit();
      }
    },
  },
  'tt-icon-button': {
    serialize: async () => ({}),
    appendFormData: async () => Promise,
    click: (event, form) => {
      switch (event.target.type) {
        case 'submit':
          form.submit();
          break;
        case 'reset':
          form.reset();
          break;
      }
    },
  },
  'tt-toggle': {
    serialize: async (el) => {
      var _a;
      if (el.name && !el.disabled) {
        return { [el.name]: el.checked ? (_a = el.value) !== null && _a !== void 0 ? _a : true : false };
      }
    },
    appendFormData: async (data, el) => {
      if (el.name && !el.disabled && el.checked) {
        data.append(el.name, el.value);
      }
    },
  },
  'tt-checkbox': {
    serialize: async (el) => {
      if (el.name && !el.disabled) {
        return { [el.name]: el.checked === 'true' ? el.value : el.checked };
      }
    },
    appendFormData: async (data, el) => {
      if (el.name && !el.disabled && el.checked === 'true') {
        data.append(el.name, el.value);
      }
    },
  },
  'tt-date-picker': {
    serialize: async (el) => {
      if (el.name && !el.disabled) {
        return { [el.name]: el.value };
      }
    },
    appendFormData: async (data, el) => {
      if (el.name && !el.disabled) {
        data.append(el.name, el.value);
      }
    },
  },
  'tt-text-field': {
    serialize: async (el) => {
      if (el.name && !el.disabled) {
        return { [el.name]: el.value };
      }
    },
    appendFormData: async (data, el) => {
      if (el.name && !el.disabled) {
        data.append(el.name, el.value);
      }
    },
    keyDown: (event, form) => {
      if (event.key === 'Enter' && !event.defaultPrevented) {
        form.submit();
      }
    },
  },
  'tt-number-field': {
    serialize: async (el) => {
      if (el.name && !el.disabled) {
        return { [el.name]: el.value };
      }
    },
    appendFormData: async (data, el) => {
      if (el.name && !el.disabled) {
        data.append(el.name, `${el.value}`);
      }
    },
    keyDown: (event, form) => {
      if (event.key === 'Enter' && !event.defaultPrevented) {
        form.submit();
      }
    },
  },
  'tt-file-input': {
    serialize: async (el) => {
      if (el.name && !el.disabled) {
        return { [el.name]: el.files };
      }
    },
    appendFormData: async (data, el) => {
      const files = el.files;
      if (el.name && !el.disabled && files) {
        Array.from(files).forEach((file) => data.append(el.name, file));
      }
    },
    appendToFormAsObject: (el) => {
      var _a;
      return (_a = el.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('input');
    },
  },
  'tt-text-area': {
    serialize: async (el) => {
      if (el.name && !el.disabled) {
        return { [el.name]: el.value };
      }
    },
    appendFormData: async (data, el) => {
      if (el.name && !el.disabled) {
        data.append(el.name, el.value);
      }
    },
  },
  'tt-radio': {
    serialize: async (el) => {
      if (el.name && el.checked && !el.disabled) {
        return { [el.name]: el.value };
      }
    },
    appendFormData: async (data, el) => {
      if (el.name && el.checked && !el.disabled) {
        data.append(el.name, el.value);
      }
    },
  },
  'tt-select': {
    serialize: async (el) => {
      const value = await el.getValue();
      if (el.name && !el.disabled) {
        return { [el.name]: value };
      }
    },
    appendFormData: async (data, el) => {
      if (el.name && !el.disabled) {
        const value = await el.getValue();
        if (el.multiple && Array.isArray(value)) {
          data.append(el.name, value.join(','));
        }
        else {
          data.append(el.name, el.value + '');
        }
      }
    },
  },
  'tt-range': {
    serialize: async (el) => {
      if (el.name && !el.disabled) {
        return { [el.name]: el.value };
      }
    },
    appendFormData: async (data, el) => {
      if (el.name && !el.disabled) {
        data.append(el.name, el.value + '');
      }
    },
  },
  'tt-wysiwyg': {
    serialize: async (el) => {
      if (el.name && !el.disabled) {
        return { [el.name]: el.value };
      }
    },
    appendFormData: async (data, el) => {
      if (el.name && !el.disabled) {
        data.append(el.name, el.value);
      }
    },
  },
  'input': {
    serialize: async (el) => {
      var _a;
      if (el.name && !el.disabled) {
        const type = el.getAttribute('type');
        if (type === 'radio' || type === 'checkbox') {
          const checked = (_a = el.checked) !== null && _a !== void 0 ? _a : false;
          if (type === 'radio' && checked) {
            return { [el.name]: el.value };
          }
          else if (type === 'checkbox') {
            return { [el.name]: checked ? [el.value] : [] };
          }
        }
        else {
          return { [el.name]: el.value };
        }
      }
    },
    appendFormData: async (data, el) => {
      if (el.name && !el.disabled) {
        // TODO: Support <input type=file> correctly for FormData
        const type = el.getAttribute('type');
        if ((type !== 'radio' && type !== 'checkbox') || el.checked) {
          data.append(el.name, el.value);
        }
      }
    },
    appendToFormAsObject: (el) => el,
  },
  'select': {
    serialize: async (el) => {
      if (el.name && !el.disabled) {
        if (el.multiple) {
          const values = [];
          for (let i = 0; i < el.options.length; i++) {
            if (el.options[i].selected) {
              values.push(el.options[i].value);
            }
          }
          return { [el.name]: values };
        }
        else {
          return { [el.name]: el.value };
        }
      }
    },
    appendFormData: async (data, el) => {
      if (el.name && !el.disabled) {
        for (let i = 0; i < el.options.length; i++) {
          if (el.options[i].selected) {
            data.append(el.name, el.options[i].value);
          }
        }
      }
    },
    appendToFormAsObject: (el) => el,
  },
};
const Form = /*@__PURE__*/ proxyCustomElement(class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.__attachShadow();
    this.ttSubmit = createEvent(this, "tt-submit", 7);
    this.action = '';
    this.label = undefined;
    this.target = '_self';
    this.method = 'get';
    this.enctype = 'application/x-www-form-urlencoded';
    this.novalidate = false;
    this.submitOnChange = false;
  }
  connectedCallback() {
    this.handleClick = this.handleClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.serializeElement = this.serializeElement.bind(this);
  }
  /** Serializes all form controls elements and returns a `FormData` object. */
  async getFormValues() {
    const formControls = await this.getFormControls();
    return formControls.reduce(async (accPromise, cur) => {
      const acc = await accPromise;
      const elementName = cur.name;
      const serializedValue = await this.serializeElement(cur);
      // If the value of serialized element is an array, we may want to append it to an existing array
      if (serializedValue && Array.isArray(serializedValue[elementName]) && acc && Array.isArray(acc[elementName])) {
        serializedValue[elementName] = [
          ...acc[elementName],
          ...serializedValue[elementName],
        ];
      }
      return Object.assign(Object.assign({}, acc), serializedValue);
    }, Promise.resolve({}));
  }
  /** Gets all form control elements (native and custom). */
  async getFormData() {
    const formData = new FormData();
    const formControls = await this.getFormControls();
    for (const formControl of formControls) {
      await this.appendFormDataElement(formData, formControl);
    }
    return formData;
  }
  /** Gets all form control elements (native and custom). */
  async getFormControls() {
    const tags = Object.keys(formControlAccessDefinition);
    return this.bodySlot
      .assignedElements({ flatten: true })
      .reduce((all, el) => all.concat(el, [...el.querySelectorAll('*')]), [])
      .filter((el) => tags.includes(el.tagName.toLowerCase()));
  }
  /**
   * Submits the form. If all controls are valid, the `sl-submit` event will be emitted and the promise will resolve
   * with `true`. If any form control is invalid, the promise will resolve with `false` and no event will be emitted.
   */
  async submit() {
    var _a;
    const formValues = await this.getFormValues();
    const formData = await this.getFormData();
    const formControls = await this.getFormControls();
    const formControlsThatReport = formControls.filter((el) => typeof el.reportValidity === 'function');
    // validate
    if (!this.novalidate) {
      const validation = await Promise.all(formControlsThatReport.map(async (el) => await el.reportValidity()));
      if (!validation.every((val) => val)) {
        return false;
      }
    }
    this.ttSubmit.emit({ formValues, formData, formControls });
    // If the 'action' property is set, we will want our form to behave much like a
    // standard <form>. To do this best, create actually create the form with reasonable
    // values and submit it.
    if (this.action.length > 0) {
      const shadowForm = document.createElement('form');
      shadowForm.setAttribute('action', this.action);
      shadowForm.setAttribute('method', this.method);
      shadowForm.setAttribute('enctype', this.enctype);
      shadowForm.setAttribute('target', this.target);
      const formControls = await this.getFormControls();
      const postSubmitCleanup = [];
      postSubmitCleanup.unshift(() => shadowForm.remove());
      for (const formControl of formControls) {
        if (this.appendToFormAsObjectElement(formControl)) {
          // For <input ...> elements, we move the domain element to the shadow
          // form. This will allow <input type=file> behaviour to keep working.
          const formElement = (_a = this.appendToFormAsObjectElement(formControl)) === null || _a === void 0 ? void 0 : _a(formControl);
          if (formElement) {
            const previousSibling = formElement.previousElementSibling;
            const nextSibling = formElement.nextElementSibling;
            const previousParent = formElement.parentNode;
            shadowForm.appendChild(formElement);
            postSubmitCleanup.unshift(() => {
              if (previousSibling) {
                previousSibling.insertAdjacentElement('afterend', formElement);
              }
              else if (nextSibling) {
                nextSibling.insertAdjacentElement('beforebegin', formElement);
              }
              else if (previousParent) {
                previousParent.appendChild(formElement);
              }
            });
          }
        }
        else {
          const name = formControl.name;
          if (name && !formControl.disabled) {
            const fd = new FormData();
            await this.appendFormDataElement(fd, formControl);
            const value = fd.get(name);
            // We can't have an element named submit, this would
            // overwrite our submit function.
            if (name === 'submit')
              continue;
            if (value === undefined || value === null)
              continue;
            const input = document.createElement('input');
            input.setAttribute('type', 'hidden');
            input.setAttribute('name', name);
            input.setAttribute('value', `${value}`);
            shadowForm.appendChild(input);
          }
        }
      }
      this.bodySlot.appendChild(shadowForm);
      shadowForm.submit();
      setTimeout(() => postSubmitCleanup.forEach((action) => action()), 1000);
    }
    return true;
  }
  /** Call this to reset the value of all form-controls in the form */
  async reset() {
    const formControls = await this.getFormControls();
    formControls.forEach((fc) => {
      var _a;
      switch (fc.tagName.toLowerCase()) {
        case 'tt-text-field':
        case 'tt-text-area':
        case 'tt-select':
          if (fc instanceof HTMLTtToggleElement || fc instanceof HTMLTtTextAreaElement) {
            fc.value = '';
          }
          else if (fc instanceof HTMLTtSelectElement) {
            fc.reset();
          }
          break;
        case 'tt-date-picker':
          fc.value = '';
          break;
        case 'tt-number-field':
          fc.value = NaN;
          break;
        case 'tt-file-input':
          fc.files = undefined;
          break;
        case 'tt-range':
          fc.value = 0;
          break;
        case 'tt-checkbox':
        case 'tt-toggle':
          fc.checked = 'false';
          break;
        case 'tt-radio':
          fc.checked = false;
          break;
        case 'input': {
          const inputFc = fc;
          if (['checkbox', 'radio'].includes((_a = inputFc.getAttribute('type')) !== null && _a !== void 0 ? _a : '')) {
            inputFc.checked = false;
          }
          else {
            inputFc.value = '';
          }
          break;
        }
      }
    });
  }
  handleClick(event) {
    var _a, _b;
    const target = event.target;
    const tag = target.tagName.toLowerCase();
    (_b = (_a = formControlAccessDefinition[tag]) === null || _a === void 0 ? void 0 : _a.click) === null || _b === void 0 ? void 0 : _b.call(_a, event, this);
  }
  handleKeyDown(event) {
    var _a, _b;
    const target = event.target;
    const tag = target.tagName.toLowerCase();
    (_b = (_a = formControlAccessDefinition[tag]) === null || _a === void 0 ? void 0 : _a.keyDown) === null || _b === void 0 ? void 0 : _b.call(_a, event, this);
  }
  handleChange() {
    if (this.submitOnChange) {
      this.submit();
    }
  }
  async serializeElement(el) {
    var _a, _b;
    const tag = el.tagName.toLowerCase();
    return (_b = (_a = formControlAccessDefinition[tag]) === null || _a === void 0 ? void 0 : _a.serialize) === null || _b === void 0 ? void 0 : _b.call(_a, el, this);
  }
  appendFormDataElement(data, el) {
    var _a, _b;
    const tag = el.tagName.toLowerCase();
    return (_b = (_a = formControlAccessDefinition[tag]) === null || _a === void 0 ? void 0 : _a.appendFormData) === null || _b === void 0 ? void 0 : _b.call(_a, data, el, this);
  }
  appendToFormAsObjectElement(el) {
    var _a;
    const tag = el.tagName.toLowerCase();
    return (_a = formControlAccessDefinition[tag]) === null || _a === void 0 ? void 0 : _a.appendToFormAsObject;
  }
  render() {
    return (h(Host, { role: "form", onClick: this.handleClick, onKeyDown: this.handleKeyDown, "onTt-change": this.handleChange, onChange: this.handleChange }, h("slot", { ref: (el) => {
        this.bodySlot = el;
      } })));
  }
  static get style() { return formCss; }
}, [1, "tt-form", {
    "action": [1],
    "label": [1],
    "target": [1],
    "method": [1],
    "enctype": [1],
    "novalidate": [4],
    "submitOnChange": [4, "submit-on-change"],
    "getFormValues": [64],
    "getFormData": [64],
    "getFormControls": [64],
    "submit": [64],
    "reset": [64]
  }]);
function defineCustomElement() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["tt-form"];
  components.forEach(tagName => { switch (tagName) {
    case "tt-form":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, Form);
      }
      break;
  } });
}

export { Form as F, defineCustomElement as d };
